import React, { useState, useEffect } from 'react';
import { PASSWORD_MIN_LENGTH, PASSWORD_MAX_LENGTH, PASSWORD_LENGTH_MESSAGE } from '@/constants/users';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { AlertMessage } from '@/components/ui/AlertMessage';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/Dialog';
interface User {
  id: number;
  email: string;
  [key: string]: any; // 다른 필드는 선택적
}

interface ChangeUserPasswordDialogProps {
  user: User | null;
  onClose: () => void;
  onSubmit: (userId: number, newPassword: string, confirmPassword: string) => Promise<void>;
}

const ChangeUserPasswordDialog: React.FC<ChangeUserPasswordDialogProps> = ({
  user,
  onClose,
  onSubmit,
}) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (user) {
      setNewPassword('');
      setConfirmPassword('');
      setError('');
      setSuccess('');
    }
  }, [user]);

  const handleSubmit = async () => {
    if (!user) return;
    
    setError('');
    setSuccess('');
    
    if (newPassword.length < PASSWORD_MIN_LENGTH || newPassword.length > PASSWORD_MAX_LENGTH) {
      setError(PASSWORD_LENGTH_MESSAGE);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }
    
    try {
      await onSubmit(user.id, newPassword, confirmPassword);
      setSuccess('비밀번호를 변경했습니다.');
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.detail || '비밀번호 변경에 실패했습니다.');
    }
  };

  return (
    <Dialog open={!!user}>
      <DialogContent className="sm:max-w-[425px]" onClose={onClose}>
        <DialogHeader>
          <DialogTitle>비밀번호 변경</DialogTitle>
          <DialogDescription>
            {user?.email} 사용자의 새 비밀번호를 설정합니다.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          {error && (
            <AlertMessage 
              type="error" 
              message={error} 
              onClose={() => {}} 
            />
          )}
          {success && (
            <AlertMessage 
              type="success" 
              message={success} 
              onClose={() => {}} 
            />
          )}
          <div className="space-y-2">
            <Label htmlFor="new_password">새 비밀번호</Label>
            <Input
              id="new_password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="새 비밀번호 입력"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm_password">비밀번호 확인</Label>
            <Input
              id="confirm_password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="비밀번호 확인"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            취소
          </Button>
          <Button onClick={handleSubmit} disabled={!newPassword || !confirmPassword}>
            변경
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ChangeUserPasswordDialog;

